import {
  CASE_UPDATE_FILE_SEARCH_HISTORY_ITEM_BEGIN,
  CASE_UPDATE_FILE_SEARCH_HISTORY_ITEM_SUCCESS,
  CASE_UPDATE_FILE_SEARCH_HISTORY_ITEM_FAILURE,
  CASE_UPDATE_FILE_SEARCH_HISTORY_ITEM_DISMISS_FEEDBACK,
} from './constants';
import { useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';
import api from 'common/api';
import { withCurrentCaseId } from 'common/selectors';
import { replaceItemImmutable } from 'utils/arrays';

export const updateFileSearchHistoryItem = createPromiseAction(
  CASE_UPDATE_FILE_SEARCH_HISTORY_ITEM_BEGIN,
);

export function dismissUpdateFileSearchHistoryItemFeedback() {
  return {
    type: CASE_UPDATE_FILE_SEARCH_HISTORY_ITEM_DISMISS_FEEDBACK,
  };
}

export function* doUpdateFileSearchHistoryItem(action) {
  const {
    payload: { caseId, searchHistoryItem },
  } = action;

  let res = yield call(api.put, `/cases/${caseId}/search-history/${searchHistoryItem.id}`, {
    archive: searchHistoryItem.archive,
    name: searchHistoryItem.name,
  });

  if (res && res.error) {
    yield put({
      type: CASE_UPDATE_FILE_SEARCH_HISTORY_ITEM_FAILURE,
      feedback: {
        message: 'feedback.updateFileSearchHistoryItemFailure',
        error: res.error,
        retryAction: action,
      },
    });
    return yield call(rejectPromiseAction, action, res.error);
  }

  yield put({
    type: CASE_UPDATE_FILE_SEARCH_HISTORY_ITEM_SUCCESS,
    data: { searchHistoryItem: res },
    payload: { searchHistoryItem },
  });

  yield call(resolvePromiseAction, action, res);
}

export function* watchUpdateFileSearchHistoryItem() {
  yield takeLatest(updateFileSearchHistoryItem, withCurrentCaseId(doUpdateFileSearchHistoryItem));
}

export function useUpdateFileSearchHistoryItem() {
  const dispatch = useDispatch();

  const { updateFileSearchHistoryItemPending, updateFileSearchHistoryItemFeedback } = useSelector(
    (state) => ({
      updateFileSearchHistoryItemPending: state.case.updateFileSearchHistoryItemPending,
      updateFileSearchHistoryItemFeedback: state.case.updateFileSearchHistoryItemFeedback,
    }),
    shallowEqual,
  );

  const boundAction = useCallback(
    (...args) => {
      return dispatch(updateFileSearchHistoryItem(...args));
    },
    [dispatch],
  );

  const boundDismissFeedback = useCallback(() => {
    return dispatch(dismissUpdateFileSearchHistoryItemFeedback());
  }, [dispatch]);

  return {
    updateFileSearchHistoryItem: boundAction,
    updateFileSearchHistoryItemPending,
    updateFileSearchHistoryItemFeedback,
    dismissUpdateFileSearchHistoryItemFeedback: boundDismissFeedback,
  };
}

export function updateFileSearchHistoryItemReducer(state, action) {
  const getIndex = (itmId) =>
    state.fileSearchHistory && state.fileSearchHistory.findIndex(({ id }) => id === itmId);

  switch (action.type) {
    case CASE_UPDATE_FILE_SEARCH_HISTORY_ITEM_BEGIN:
      return {
        ...state,
        updateFileSearchHistoryItemPending: true,
        updateFileSearchHistoryItemFeedback: null,
      };

    case CASE_UPDATE_FILE_SEARCH_HISTORY_ITEM_SUCCESS:
      return {
        ...state,
        updateFileSearchHistoryItemPending: false,
        updateFileSearchHistoryItemFeedback: null,
        fileSearchHistory:
          getIndex(action.payload.searchHistoryItem.id) >= 0
            ? replaceItemImmutable(
                Array.isArray(state.fileSearchHistory) ? state.fileSearchHistory : [],
                action.data.searchHistoryItem,
                getIndex(action.payload.searchHistoryItem.id),
              )
            : state.fileSearchHistory,
      };

    case CASE_UPDATE_FILE_SEARCH_HISTORY_ITEM_FAILURE:
      return {
        ...state,
        updateFileSearchHistoryItemPending: false,
        updateFileSearchHistoryItemFeedback: action.feedback,
      };

    case CASE_UPDATE_FILE_SEARCH_HISTORY_ITEM_DISMISS_FEEDBACK:
      return {
        ...state,
        updateFileSearchHistoryItemFeedback: null,
      };

    default:
      return state;
  }
}
